import User from './User';
import { InvalidLoginStrategyError } from './Authenticator/AuthenticationError';
import { AuthenticatorStorage } from './Authenticator/AuthenticatorStorage';

// Utility types
type MakeOptionalParams<P> = P extends undefined ? [undefined?] : [P]
type MakeLoginWithParams<T extends AuthenticatorStrategy> = MakeOptionalParams<Parameters<T['login']>[0]>

export interface FetchUserFnReturnType {
  user: User | null;
  error: Error | null;
}

export interface AuthenticatorStrategy {
  login: <T>(data: T) => Promise<void> | void;
}

export interface FetchUserFnData {
  token: string;
}
export type AuthStrategyOptions = {
  [name: string]: AuthenticatorStrategy;
}

export interface AuthenticatorOptions<StrategyOptionsT extends AuthStrategyOptions = AuthStrategyOptions> {
  handler: {
    fetchUser?: (data: FetchUserFnData) => Promise<FetchUserFnReturnType>;
    logout?: () => Promise<void>;
  };
  strategy: StrategyOptionsT;
  storage: AuthenticatorStorage;
}

export interface AuthenticatorInitOptions {
  /**
   * Initialize with token. If null is provided, this will initialize loginController for guest user instead.
   */
  token?: string | null;
}

export class Authenticator<OptionsT extends AuthenticatorOptions = AuthenticatorOptions> {
  private accessToken: string | null = null;

  private fetchUserPromise: Promise<FetchUserFnReturnType> | null = null;

  protected options: OptionsT;

  constructor(options: OptionsT) {
    this.options = {
      ...options,
      handler: options.handler ?? {},
    };
  }

  /**
   * Initialize Authenticator. It will sync and process access token, and fetch user object if fetchUser if provided).
   * This method can be called again to rerun the process.
   */
  async init(options?: AuthenticatorInitOptions): Promise<void> {
    const opts: AuthenticatorInitOptions = {
      ...options,
    };

    // Set token eagerly.
    // Some legacy implementation expect token to be presence immediatey
    this.setToken(
      (opts.token === null)
        ? null // initialize loginController for guest user
        : (opts.token ?? this.options.storage.get()),
    );

    if (this.token == null) {
      this.setUser(null);
      return;
    }

    this.fetchUserPromise = this.fetchUser({ token: this.token });

    const { error } = await this.fetchUserPromise;

    if (error) {
      this.setToken(null);
    }
  }

  loginWith<StrategyT extends OptionsT['strategy'], K extends keyof StrategyT, R extends ReturnType<StrategyT[K]['login']>>(
    method: K,
    ...payload: MakeLoginWithParams<StrategyT[K]>
  ): R {
    const strategy = (this.options.strategy as StrategyT)[method];
    if (!strategy) {
      throw new InvalidLoginStrategyError();
    }

    const p0 = (payload as any[])[0];
    return strategy.login(p0) as R;
  }

  async getUser(shouldRefetchUser = false): Promise<User | null> {
    if (!this.fetchUserPromise) {
      throw new Error('UseBeforeInitializeError');
    }

    if (shouldRefetchUser) {
      await this.init();
    }

    const { user } = await this.fetchUserPromise;
    return user;
  }

  /**
   * Manually set the user object.
   * This method should be used if fetchUser handler is not provided
   * @param user
   */
  setUser(user: User | null): void {
    this.fetchUserPromise = Promise.resolve({
      user,
      error: null,
    });
  }

  /**
   * Logout user and reload the page.
   *
   * @param redirectionURL value to override the reload behaviour by supplying an alternative URL to redirect to after logout.
   * Otherwise a boolean `false` can be provided to disable reload.
   * @param preRedirectionCalback callback to execute before redirection/reload occur.
   * If the callback returns a Promise, the function will wait until it get resolved before redirect/reload
   */
  async logout(redirectionURL?: string | false, preRedirectionCalback?: () => any): Promise<void> {
    // Perform proper removal if user exist
    const user = await this.getUser();
    if (user) {
      // The use of this window as any is because the typing from @types/google.accounts was not recognised by the typescript
      (window as any).google?.accounts?.id?.disableAutoSelect();
      user.destroy();
      // window.OneSignal.push(() => {
      //   window.OneSignal.deleteTags(['user_id', 'support_points_system']);
      // });
      this.options.handler.logout?.();
    } else {
      // Forcefully call destroy again.
      // This is done for legacy support as some of the user data might be stored in localStorage
      new User({}).destroy();
    }

    this.setUser(null);
    this.setToken(null);

    try {
      await preRedirectionCalback?.();
    } catch (e) {
      // Do nothing and continue with reload / redirect
    }

    if (redirectionURL === false) return; // skip redirection / reload

    if (redirectionURL && location.href !== redirectionURL) {
      location.replace(redirectionURL);
    } else {
      location.reload();
    }
  }

  get token(): string | null {
    return this.accessToken;
  }

  private setToken(token: string | null): void {
    if (token == null) {
      this.accessToken = null;
      this.options.storage.clear();
      return;
    }

    this.accessToken = token;
    this.options.storage.set(token);
  }

  private async fetchUser(data: FetchUserFnData): Promise<FetchUserFnReturnType> {
    const payload = await this.options.handler.fetchUser?.(data);

    if (payload) return payload;

    return {
      user: null,
      error: null,
    };
  }
}
